interface Range {
  min?: number;
  max?: number;
}

const finiteNumber = (value: unknown): number | undefined => {
  const parsed = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : undefined;
};

/** Resolve a range to the single value used by target/progress displays. */
export const resolveRangeMidpoint = (
  min: unknown,
  max: unknown,
  fallback?: number,
): number | undefined => {
  const normalizedMin = finiteNumber(min);
  const normalizedMax = finiteNumber(max);

  if (normalizedMin !== undefined && normalizedMax !== undefined) {
    return (normalizedMin + normalizedMax) / 2;
  }
  return normalizedMin ?? normalizedMax ?? fallback;
};

export const normalizeRange = (value?: number, min?: number, max?: number): Range => {
  if (typeof min === 'number' && typeof max === 'number') {
    return { min, max };
  }

  if (typeof value === 'number') {
    return { min: value, max: value };
  }

  if (typeof min === 'number') {
    return { min, max: min };
  }

  if (typeof max === 'number') {
    return { min: max, max };
  }

  return { min: undefined, max: undefined };
};

export interface NutritionGoalsInput {
  calories?: number;
  protein?: number;
  proteinMin?: number;
  proteinMax?: number;
  carbs?: number;
  carbsMin?: number;
  carbsMax?: number;
  fat?: number;
  fatMin?: number;
  fatMax?: number;
  fiber?: number;
}

export const buildNutritionSummaryGoals = (goals?: NutritionGoalsInput) => {
  if (!goals) return undefined;

  const normalizedProtein = normalizeRange(goals.protein, goals.proteinMin, goals.proteinMax);
  const normalizedCarbs = normalizeRange(goals.carbs, goals.carbsMin, goals.carbsMax);
  const normalizedFat = normalizeRange(goals.fat, goals.fatMin, goals.fatMax);

  return {
    calories: goals.calories,
    proteinMin: normalizedProtein.min,
    proteinMax: normalizedProtein.max,
    carbsMin: normalizedCarbs.min,
    carbsMax: normalizedCarbs.max,
    fatMin: normalizedFat.min,
    fatMax: normalizedFat.max,
    fiber: goals.fiber,
  };
};

type NutritionMetricKey = 'calories' | 'protein' | 'carbs' | 'fat' | 'fiber';

const nutritionMetrics: Array<{ key: NutritionMetricKey; label: string }> = [
  { key: 'calories', label: 'Calories' },
  { key: 'protein', label: 'Protein' },
  { key: 'carbs', label: 'Carbs' },
  { key: 'fat', label: 'Fat' },
  { key: 'fiber', label: 'Fiber' },
];

const formatSingleValue = (value?: number, suffix = '') => {
  if (typeof value !== 'number') {
    return '—';
  }
  return `${Math.round(value)}${suffix ? ` ${suffix}` : ''}`;
};

const formatRangeValue = (min?: number, max?: number, suffix = '') => {
  if (typeof min === 'number' && typeof max === 'number') {
    if (min === max) {
      return `${Math.round(min)}${suffix ? ` ${suffix}` : ''}`;
    }
    return `${Math.round(min)} - ${Math.round(max)}${suffix ? ` ${suffix}` : ''}`;
  }

  if (typeof min === 'number') {
    return `${Math.round(min)}${suffix ? ` ${suffix}` : ''}`;
  }

  if (typeof max === 'number') {
    return `${Math.round(max)}${suffix ? ` ${suffix}` : ''}`;
  }

  return '—';
};

export const buildGoalDisplayItems = (goals?: NutritionGoalsInput | null) => {
  if (!goals) return [];

  const normalizedProtein = normalizeRange(goals.protein, goals.proteinMin, goals.proteinMax);
  const normalizedCarbs = normalizeRange(goals.carbs, goals.carbsMin, goals.carbsMax);
  const normalizedFat = normalizeRange(goals.fat, goals.fatMin, goals.fatMax);

  const goalDisplays: Record<NutritionMetricKey, string> = {
    calories: formatSingleValue(goals.calories, 'kcal'),
    protein: formatRangeValue(normalizedProtein.min, normalizedProtein.max, 'g'),
    carbs: formatRangeValue(normalizedCarbs.min, normalizedCarbs.max, 'g'),
    fat: formatRangeValue(normalizedFat.min, normalizedFat.max, 'g'),
    fiber: formatSingleValue(goals.fiber, 'g'),
  };

  return nutritionMetrics.map(({ key, label }) => ({
    key,
    label,
    value: goalDisplays[key],
  }));
};
